import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, query, where, onSnapshot, limit } from 'firebase/firestore';
import * as LucideIcons from 'lucide-react';
import { useAppContext } from '../AppContext';
import LoadingSpinner from './LoadingSpinner';
import DreamWeaveAIChat from './DreamWeave/DreamWeaveAIChat';
import useDreamWeaveData from '../hooks/useDreamWeaveData';
import DreamWeaveInfo from './DreamWeave/DreamWeaveInfo';
import DreamWeaveEvents from './DreamWeave/DreamWeaveEvents';
import GameViewer from './DreamWeave/GameViewer';
import DreamWeaveTutorial from './DreamWeave/DreamWeaveTutorial';
import NotificationManager from './DreamWeave/NotificationManager';
import { triggerHaptic } from '../utils/haptics';
import { useTranslation } from 'react-i18next';
import DailyArcadeChallenge from './DreamWeave/DailyArcadeChallenge';
import CoopRaidBoss from './DreamWeave/CoopRaidBoss';

// Lazy load heavy components
const ModularStation = React.lazy(() => import('./DreamWeave/games/modular-station/ModularStation'));
const PlasmaHawk = React.lazy(() => import('./DreamWeave/games/plasma-hawk/PlasmaHawk'));
const VelocityApex = React.lazy(() => import('./DreamWeave/games/velocity-apex/VelocityApex'));
const IronColiseum = React.lazy(() => import('./DreamWeave/games/iron-coliseum/IronColiseum'));
const VoidHorizon = React.lazy(() => import('./DreamWeave/games/void-horizon/VoidHorizon'));
const AsteroidBlaster = React.lazy(() => import('./DreamWeave/games/asteroid-blaster/AsteroidBlaster'));
const NeuroMatch = React.lazy(() => import('./DreamWeave/games/neuro-match/NeuroMatch'));
const ChromaClash = React.lazy(() => import('./DreamWeave/games/chroma-clash/ChromaClash'));
const DreamWeaveVisualization = React.lazy(() => import('./DreamWeave/DreamWeaveVisualization'));
const AuraEater = React.lazy(() => import('./DreamWeave/games/aura-eater/AuraEater'));
const SynapticStacking = React.lazy(() => import('./DreamWeave/SynapticStacking'));
const SynapticSerpent = React.lazy(() => import('./DreamWeave/games/synaptic-serpent'));
const CosmicAscent = React.lazy(() => import('./DreamWeave/CosmicAscent'));
const StarlightSentinel = React.lazy(() => import('./DreamWeave/games/starlight-sentinel/StarlightSentinel'));
const HarmonicResonance = React.lazy(() => import('./DreamWeave/games/harmonic-resonance/HarmonicResonance'));
const Steelecho = React.lazy(() => import('./DreamWeave/games/steelecho/Steelecho'));
const ZenithProtocol = React.lazy(() => import('./DreamWeave/games/zenith-protocol/ZenithProtocol'));

const gameComponents = {
    'modular-station': ModularStation,
    'plasma-hawk': PlasmaHawk,
    'velocity-apex': VelocityApex,
    'iron-coliseum': IronColiseum,
    'void-horizon': VoidHorizon,
    'asteroid-blaster': AsteroidBlaster,
    'neuro-match': NeuroMatch,
    'chroma-clash': ChromaClash,
    auraEater: AuraEater,
    synapticStacking: SynapticStacking,
    synapticSerpent: SynapticSerpent,
    cosmicAscent: CosmicAscent,
    starlightSentinel: StarlightSentinel,
    harmonicResonance: HarmonicResonance,
    steelecho: Steelecho,
    'zenith-protocol': ZenithProtocol
};

const BottomNavItem = ({ title, icon, onClick, isActive }) => {
    const Icon = LucideIcons[icon];
    return (
        <button
            onClick={onClick}
            aria-label={title}
            className={`flex flex-col items-center justify-center w-1/4 pt-2 pb-1 text-xs font-medium transition-all duration-300 ${isActive ? 'text-cyan-400 scale-110' : 'text-gray-400 hover:text-white'} active:scale-95`}
        >
            <Icon size={24} className={`mb-1 transition-all duration-300 ${isActive ? 'drop-shadow-[0_0_8px_rgba(34,211,238,0.8)]' : ''}`} />
            <span className={isActive ? 'font-bold text-cyan-100' : ''}>{title}</span>
        </button>
    );
};

const ContributionMethodButton = ({ title, description, onSelect, icon }) => {
    const Icon = LucideIcons[icon];
    return (
        <button
            onClick={onSelect}
            className="group flex flex-col items-center justify-center text-center p-6 border border-white/10 rounded-xl bg-white/5 backdrop-blur-md text-white cursor-pointer transition-all duration-300 shadow-lg hover:-translate-y-1 hover:shadow-cyan-500/20 hover:border-cyan-500/50 h-full relative overflow-hidden"
        >
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <Icon className="w-10 h-10 text-cyan-400 mb-3 group-hover:scale-110 transition-transform duration-300" />
            <h4 className="font-bold text-white text-lg font-orbitron mb-2">{title}</h4>
            <p className="text-xs text-gray-300 mt-1 flex-grow leading-relaxed">{description}</p>
        </button>
    );
};

const DreamWeaveSkeleton = () => (
    <div className="relative w-full h-[80vh] bg-gray-900/90 rounded-lg flex flex-col overflow-hidden animate-pulse border border-white/5">
        {/* Main Content Area Skeleton */}
        <div className="flex-grow bg-white/5 m-4 rounded-lg"></div>
        
        {/* Header Skeleton */}
        <div className="absolute top-4 right-4 bg-white/10 p-2 rounded-full flex items-center space-x-2 h-8 w-24">
        </div>

        {/* Bottom Navigation Bar Skeleton */}
        <div className="absolute bottom-4 left-4 right-4 h-16 bg-black/40 backdrop-blur-md rounded-2xl border border-white/10 flex justify-around z-20 items-center">
            <div className="h-8 w-8 bg-white/10 rounded-full"></div>
            <div className="h-8 w-8 bg-white/10 rounded-full"></div>
            <div className="h-8 w-8 bg-white/10 rounded-full"></div>
            <div className="h-8 w-8 bg-white/10 rounded-full"></div>
            <div className="h-8 w-8 bg-white/10 rounded-full"></div>
        </div>
    </div>
);

function NexusDreamWeave({ nexusId, nexusData }) {
    const { t } = useTranslation();
    const { appFunctions, db, appId, userId } = useAppContext();
    const [activeTab, setActiveTab] = useState('visualization');
    const [selectedArcadeGame, setSelectedArcadeGame] = useState(null);
    const [dreamWeaveId, setDreamWeaveId] = useState(null);
    const [souls, setSouls] = useState(0);
    const [soulsAwarded, setSoulsAwarded] = useState(0);
    const [isCreating, setIsCreating] = useState(false);
    const [isFetchingId, setIsFetchingId] = useState(true);
    const [isEventsDrawerOpen, setIsEventsDrawerOpen] = useState(false);
    const [showTutorial, setShowTutorial] = useState(false);

    useEffect(() => {
        const tutorialCompleted = localStorage.getItem('dreamWeaveTutorialCompleted');
        if (!tutorialCompleted) {
            setShowTutorial(true);
        }
    }, []);

    const handleFinishTutorial = () => {
        localStorage.setItem('dreamWeaveTutorialCompleted', 'true');
        setShowTutorial(false);
    };

    const handleGameSubmit = useCallback(async (gameId, score, gameLog) => {
        if (!dreamWeaveId || !nexusId) {
            console.error("Missing dreamWeaveId or nexusId");
            return;
        }
        
        const functionName = `submit${gameId.charAt(0).toUpperCase() + gameId.slice(1)}Game`;
        const submitFunction = httpsCallable(appFunctions, functionName);

        try {
            const result = await submitFunction({ dreamWeaveId, nexusId, score, gameLog });
            if (result.data.success && result.data.soulsAwarded > 0) {
                triggerHaptic('success');
                setSoulsAwarded(result.data.soulsAwarded);
                setTimeout(() => setSoulsAwarded(0), 3000);
            }
            return result.data;
        } catch (error) {
            triggerHaptic('error');
            console.error(`Error submitting score for ${gameId}:`, error);
            throw error;
        }
    }, [appFunctions, dreamWeaveId, nexusId]);

    useEffect(() => {
        if (activeTab !== 'contribute') setSelectedArcadeGame(null);
    }, [activeTab]);

    useEffect(() => {
        if (!db || !nexusId || !appId) {
            setIsFetchingId(false);
            return;
        }
        setIsFetchingId(true);
        const q = query(
            collection(db, `artifacts/${appId}/public/data/dreamWeaves`),
            where("nexusId", "==", nexusId),
            where("status", "==", "active"),
            limit(1)
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
            if (!snapshot.empty) {
                const dreamWeave = snapshot.docs[0].data();
                setDreamWeaveId(snapshot.docs[0].id);
                setSouls(dreamWeave.souls || 0);
            } else {
                setDreamWeaveId(null);
                setSouls(0);
            }
            setIsFetchingId(false);
        }, (err) => {
            console.error("Error fetching active DreamWeave ID:", err);
            setIsFetchingId(false);
        });
        return () => unsubscribe();
    }, [db, nexusId, appId]);

    const { quests, event, loading, error } = useDreamWeaveData(dreamWeaveId, nexusId);

    const handleCreateDreamWeave = async () => {
        setIsCreating(true);
        try {
            const createNexusDreamWeave = httpsCallable(appFunctions, 'createNexusDreamWeave');
            const result = await createNexusDreamWeave({ nexusId, title: t('nexusDreamWeave.defaultTitle'), theme: t('nexusDreamWeave.defaultTheme') });
            if (result.data.success) {
                setDreamWeaveId(result.data.dreamWeaveId);
            }
        } catch (err) {
            console.error("Error creating Nexus DreamWeave:", err);
        } finally {
            setIsCreating(false);
        }
    };

    const isOwner = nexusData?.ownerId === userId;
    const releaseDate = new Date('2025-12-01');
    const isFeatureLocked = new Date() < releaseDate;

    if (isFeatureLocked && !isOwner) {
        return (
            <div className="flex flex-col items-center justify-center p-8 bg-gray-900/70 rounded-lg text-white">
                <LucideIcons.Lock size={64} className="text-yellow-400 mb-4" />
                <h2 className="text-2xl font-bold mb-4">Feature Locked</h2>
                <p className="text-center mb-6">This feature is currently available only to the Nexus owner until December 1st, 2025.</p>
            </div>
        );
    }

    if (isFetchingId) return <DreamWeaveSkeleton />;
    if (error) return <div className="text-red-400 text-center p-8">{t('nexusDreamWeave.error')}: {error.message}</div>;

    if (!dreamWeaveId) {
        return (
            <div className="flex flex-col items-center justify-center p-8 bg-gray-900/70 rounded-lg text-white">
                <LucideIcons.BrainCircuit size={64} className="text-indigo-400 mb-4" />
                <h2 className="text-2xl font-bold mb-4">{t('nexusDreamWeave.noDreamWeave.title')}</h2>
                <p className="text-center mb-6">{t('nexusDreamWeave.noDreamWeave.subtitle')}</p>
                <button onClick={handleCreateDreamWeave} disabled={isCreating} className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-6 rounded-full">
                    {isCreating ? t('nexusDreamWeave.noDreamWeave.creating') : t('nexusDreamWeave.noDreamWeave.createButton')}
                </button>
            </div>
        );
    }

    const renderTabContent = () => {
        if (loading) return <LoadingSpinner message={t('nexusDreamWeave.loading')} />;
        
        const SelectedGameComponent = selectedArcadeGame ? gameComponents[selectedArcadeGame] : null;
        const contentWrapper = (children) => <div className="h-full w-full">{children}</div>;
        const scrollableContentWrapper = (children) => <div className="p-4 md:p-6 h-full overflow-y-auto bg-gray-900 text-white custom-scrollbar">{children}</div>;

        const handleGameSelect = (game) => {
            triggerHaptic('light');
            setSelectedArcadeGame(game);
        }

        switch (activeTab) {
            case 'visualization':
                return contentWrapper(<Suspense fallback={<LoadingSpinner />}><DreamWeaveVisualization /></Suspense>);
            case 'contribute':
                return scrollableContentWrapper(
                    <>
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-3xl font-bold text-center mb-8 text-white">Co-op Raid & Arcade</h2>
                            
                            <CoopRaidBoss nexusId={nexusId} onBossClick={() => console.log('Boss clicked')} />

                            <DailyArcadeChallenge onPlayGame={handleGameSelect} />
                            <p className="text-center text-gray-400 mb-8">{t('nexusDreamWeave.contribute.subtitle')}</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                                <ContributionMethodButton icon="Flame" title={t('nexusDreamWeave.contribute.auraEater.title')} description={t('nexusDreamWeave.contribute.auraEater.description')} onSelect={() => handleGameSelect('auraEater')} />
                                <ContributionMethodButton icon="Layers" title={t('nexusDreamWeave.contribute.synapticStacking.title')} description={t('nexusDreamWeave.contribute.synapticStacking.description')} onSelect={() => handleGameSelect('synapticStacking')} />
                                <ContributionMethodButton icon="Spline" title={t('nexusDreamWeave.contribute.synapticSerpent.title')} description={t('nexusDreamWeave.contribute.synapticSerpent.description')} onSelect={() => handleGameSelect('synapticSerpent')} />
                                <ContributionMethodButton icon="Rocket" title={t('nexusDreamWeave.contribute.cosmicAscent.title')} description={t('nexusDreamWeave.contribute.cosmicAscent.description')} onSelect={() => handleGameSelect('cosmicAscent')} />
                                <ContributionMethodButton icon="Shield" title={t('nexusDreamWeave.contribute.starlightSentinel.title')} description={t('nexusDreamWeave.contribute.starlightSentinel.description')} onSelect={() => handleGameSelect('starlightSentinel')} />
                                <ContributionMethodButton icon="Music" title={t('nexusDreamWeave.contribute.harmonicResonance.title', 'Harmonic Resonance')} description={t('nexusDreamWeave.contribute.harmonicResonance.description', 'Tune the Nexus by tapping to the rhythm of the cosmos.')} onSelect={() => handleGameSelect('harmonicResonance')} />
                                <ContributionMethodButton icon="Gamepad2" title="Chroma Clash" description="Foray into neon-shaded color territories." onSelect={() => handleGameSelect('chroma-clash')} />
                                <ContributionMethodButton icon="Gamepad2" title="Asteroid Blaster" description="Destroy space debris threatening the core." onSelect={() => handleGameSelect('asteroid-blaster')} />
                                <ContributionMethodButton icon="Orbit" title="Modular Station" description="Manage energy grids and construct orbital station modules." onSelect={() => handleGameSelect('modular-station')} />
                                <ContributionMethodButton icon="Rocket" title="Plasma Hawk" description="Pilot a tactical jet through intense space bullet hell." onSelect={() => handleGameSelect('plasma-hawk')} />
                                <ContributionMethodButton icon="Gauge" title="Velocity Apex" description="Outrun the clock in high-speed racer trials." onSelect={() => handleGameSelect('velocity-apex')} />
                                <ContributionMethodButton icon="ShieldAlert" title="Iron Coliseum" description="Steer battle mechs and clear gladiator arena waves." onSelect={() => handleGameSelect('iron-coliseum')} />
                                <ContributionMethodButton icon="Compass" title="Void Horizon" description="Navigate zero gravity mining belts to secure cargo." onSelect={() => handleGameSelect('void-horizon')} />
                                <ContributionMethodButton icon="BrainCircuit" title="Neuro Match" description="Hack cybernetic protocols by matching code keys." onSelect={() => handleGameSelect('neuro-match')} />
                                <ContributionMethodButton icon="EyeOff" title="Steel Echo" description="Infiltrate maze corridors stealthily avoiding drone scans." onSelect={() => handleGameSelect('steelecho')} />
                                <ContributionMethodButton icon="Radio" title="Zenith Protocol" description="Defend the central CPU from malware virus code waves." onSelect={() => handleGameSelect('zenith-protocol')} />
                            </div>
                        </div>
                        {SelectedGameComponent && (
                            <GameViewer
                                dreamWeaveId={dreamWeaveId}
                                nexusId={nexusId}
                                onClose={() => setSelectedArcadeGame(null)}
                                gameComponent={<Suspense fallback={<LoadingSpinner />}><SelectedGameComponent souls={souls} onGameSubmit={handleGameSubmit} /></Suspense>}
                            />
                        )}
                    </>
                );
            case 'aiChat':
                return contentWrapper(<DreamWeaveAIChat dreamWeaveId={dreamWeaveId} nexusId={nexusId} />);
            case 'info':
                return scrollableContentWrapper(<DreamWeaveInfo setActiveTab={setActiveTab} />);
            default:
                return null;
        }
    };

    return (
        <div className="relative w-full h-[80vh] bg-gradient-to-b from-slate-900/90 to-black/95 backdrop-blur-xl border border-white/10 rounded-xl flex flex-col overflow-hidden shadow-2xl">
            {showTutorial && <DreamWeaveTutorial onFinish={handleFinishTutorial} />}
            {/* Events Drawer */}
            {isEventsDrawerOpen && (
                <div 
                    className="absolute inset-0 bg-black/60 z-30 backdrop-blur-sm" 
                    onClick={() => setIsEventsDrawerOpen(false)}
                ></div>
            )}
            <div className={`absolute top-0 left-0 h-full w-80 bg-gray-900/95 backdrop-blur-xl border-r border-white/10 z-40 transform transition-transform duration-300 ease-in-out shadow-2xl ${isEventsDrawerOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                <DreamWeaveEvents 
                    quests={quests} 
                    event={event} 
                    setActiveTab={(tab) => {
                        setActiveTab(tab);
                        setIsEventsDrawerOpen(false);
                    }} 
                />
            </div>

            <div className="flex-grow relative min-h-0 pb-24 animate-fadeIn" key={activeTab}>{renderTabContent()}</div>
            
            <div className="absolute top-4 left-4">
                <NotificationManager />
            </div>

            <div className="absolute top-4 right-4 bg-black/40 backdrop-blur-md border border-white/10 px-3 py-1.5 rounded-full text-center flex items-center space-x-2 cursor-pointer hover:bg-white/10 transition-all">
                <LucideIcons.Sparkles size={16} className="text-purple-400" />
                <div className="text-sm font-bold text-purple-300 font-orbitron">{souls}</div>
                <div className="text-[10px] text-gray-400 pr-1 uppercase tracking-wider">{t('nexusDreamWeave.souls')}</div>
            </div>
            
            {soulsAwarded > 0 && (
                <div key={Date.now()} className="absolute top-16 right-4 bg-green-500/80 backdrop-blur-md text-white px-4 py-2 rounded-xl border border-green-400/30 animate-pop-and-fade font-bold shadow-lg">
                    +{soulsAwarded} {t('nexusDreamWeave.souls')}
                </div>
            )}

            {/* Bottom Navigation Bar */}
            <div className="absolute bottom-4 left-4 right-4 h-16 bg-black/60 backdrop-blur-xl rounded-2xl border border-white/10 flex justify-around z-20 shadow-lg items-center">
                <BottomNavItem title={t('nexusDreamWeave.nav.visualize')} icon="LayoutGrid" onClick={() => setActiveTab('visualization')} isActive={activeTab === 'visualization'} />
                <BottomNavItem title={t('nexusDreamWeave.nav.contribute')} icon="Gamepad2" onClick={() => setActiveTab('contribute')} isActive={activeTab === 'contribute'} />
                <BottomNavItem title={t('nexusDreamWeave.nav.quests')} icon="Target" onClick={() => setIsEventsDrawerOpen(true)} isActive={isEventsDrawerOpen} />
                <BottomNavItem title={t('nexusDreamWeave.nav.aiChat')} icon="Bot" onClick={() => setActiveTab('aiChat')} isActive={activeTab === 'aiChat'} />
                <BottomNavItem title={t('nexusDreamWeave.nav.info')} icon="Info" onClick={() => setActiveTab('info')} isActive={activeTab === 'info'} />
            </div>
        </div>
    );
}

export default NexusDreamWeave;